import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Button, Col, Row } from "react-bootstrap";
import { ArrowRight } from "react-bootstrap-icons";
import classNames from "classnames";
import { Link } from "gatsby";

import GatsbyImageWithSVGSupport from "./GatsbyImageWithSVGSupport";
import BusinessInfoModalForm from "./BusinessInfoModalForm";

import { openModal, selectModalId } from "../redux/slices/businessModalSlice";

import "../styles/components/BackgroundImageCards.scss";

const CTAButton = ({ modalId, gatedForm, downloadPdf, link, linkText }) => {
    const dispatch = useDispatch();

    const ctaProps = {
        className: "cta-button",
        label: linkText || "Access",
        variant: "link",
    };

    if (gatedForm && downloadPdf) {
        ctaProps["aria-label"] = "Open gated form";
        ctaProps.onClick = () =>
            dispatch(
                openModal({
                    modalId,
                    downloadPdfUrl: downloadPdf.file.url,
                    source: gatedForm.toLowerCase().replace(/\s+/g, "_"),
                }),
            );
    } else if (downloadPdf) {
        ctaProps.as = "a";
        ctaProps["aria-label"] = downloadPdf.fileName;
        ctaProps.href = downloadPdf.file.url;
        ctaProps.target = "_blank";
        ctaProps.rel = "noreferrer";
    } else if (link?.startsWith("/")) {
        ctaProps.as = Link;
        ctaProps.to = link;
    } else if (link) {
        ctaProps.as = "a";
        ctaProps.href = link;
    } else {
        return null;
    }

    return (
        <Button {...ctaProps}>
            {ctaProps.label} <ArrowRight size={20} className="ms-2" />
        </Button>
    );
};

CTAButton.propTypes = {
    modalId: PropTypes.string,
    downloadPdf: PropTypes.shape({
        description: PropTypes.string,
        file: PropTypes.shape({
            url: PropTypes.string.isRequired,
            fileName: PropTypes.string,
        }),
    }),
    gatedForm: PropTypes.string,
    linkText: PropTypes.string,
    link: PropTypes.string,
};

const BackgroundImageCards = ({ cards }) => {
    const modalId = useSelector(selectModalId);
    const ref = useRef(null);
    const [hasAnimated, setHasAnimated] = useState(false);

    useEffect(() => {
        const element = ref.current;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated) {
                    setHasAnimated(true);
                    observer.disconnect(); // Stop observing after animation is triggered
                }
            },
            { threshold: 0.2 },
        );

        if (element) {
            observer.observe(element);
        }

        // Cleanup function using the captured local variable
        return () => {
            if (element) {
                observer.unobserve(element);
            }
        };
    }, [hasAnimated]);

    if (!cards?.length) return null;

    return (
        <Row
            ref={ref}
            className={classNames("background-image-cards mb-5", {
                "fade-in-up-animation": hasAnimated,
            })}
        >
            {cards.map(card => (
                <Col key={card.title} lg={4}>
                    <div className="background-image-card my-3 my-lg-0">
                        <GatsbyImageWithSVGSupport
                            className="image ratio ratio-4x3"
                            image={card.image}
                        />
                        <div className="background-image-overlay p-4">
                            <h3>{card.title}</h3>
                            <CTAButton
                                gatedForm={card.gatedForm}
                                downloadPdf={card.downloadPdf}
                                link={card.link}
                                linkText={card.linkText}
                                modalId={card.id}
                            />
                        </div>
                        {modalId === card.id && <BusinessInfoModalForm />}
                    </div>
                </Col>
            ))}
        </Row>
    );
};

BackgroundImageCards.propTypes = {
    cards: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            title: PropTypes.string.isRequired,
            image: PropTypes.shape({ gatsbyImageData: PropTypes.object })
                .isRequired,
            downloadPdf: PropTypes.shape({
                description: PropTypes.string,
                file: PropTypes.shape({
                    url: PropTypes.string.isRequired,
                    fileName: PropTypes.string,
                }),
            }),
            gatedForm: PropTypes.string,
            linkText: PropTypes.string,
            link: PropTypes.string,
        }),
    ),
};

export default BackgroundImageCards;
